import cx from 'clsx';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { ReactComponent as CopyIcon } from '@assets/svg/ic-copyDetails.svg';

import ButtonCopyDetails from '@components/common/ButtonCopyDetails/ButtonCopyDetails';
import Gallery from '@components/common/Gallery/Gallery';
import InfoField from '@components/common/InfoField/InfoField';
import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';
import TreeSelect from '@components/uikit/TreeSelect/TreeSelect';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IEquipmentDetails } from '@model/equipmentDetails';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useInstallationPointPhotosQuery } from '@services/useInstallationPointPhotosQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { EQUIPMENT_DETAILS_FIELDS, type IFormEquipmentDetails } from '../../constants/equipmentDetailsFields';
import { useLocationChangeMutation } from '../../services/useLocationChangeMutation';
import EquipmentDetailsType from '../EquipmentDetailsType/EquipmentDetailsType';
import EquipmentDetailsInformationSkeleton from './EquipmentDetailsInformationSkeleton';
import style from './style.module.scss';

interface IProps {
  detailsData?: IEquipmentDetails;
  copyDetailsData?: string[];
  locationName?: string;
  isEquipmentLoading?: boolean;
}

const EquipmentDetailsInformation: React.FC<IProps> = ({
  detailsData,
  copyDetailsData,
  locationName = '',
  isEquipmentLoading = false,
}) => {
  const { t } = useTranslation();
  const { equipmentType, itemId } = useParams();

  const { mutate: locationChangeMutate, isLoading: locationChangeIsLoading } = useLocationChangeMutation();

  const { setActiveModal } = useModalActions();

  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: profileData } = useUserProfileQuery();
  const { data: imagesData, isFetching: isFetchingImages } = useInstallationPointPhotosQuery({
    installationPointId: itemId,
    enabled: !!itemId && equipmentType === 'tachometer',
  });

  const hasPermissions = profileData?.userAccess?.manageEquipmentDeactivation;

  const facilityId = detailsData?.facility_id || detailsData?.gateway?.facility?.facility_id;
  const locationId =
    detailsData?.location_id || detailsData?.equipment?.locationTable?.id || detailsData?.sensors_location_id;
  const facilityLevel = assetTreeData?.facilities || [];
  const currentFacility = facilityId
    ? facilityLevel.find((facility) => +facility.facilityId === +facilityId)
    : null;
  const selectOptions = currentFacility?.children || [];
  const imagesList = equipmentType === 'tachometer' ? imagesData : detailsData?.photos;

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = useForm<IFormEquipmentDetails>({
    mode: 'all',
    defaultValues: {
      [EQUIPMENT_DETAILS_FIELDS.LOCATION_INFO]: detailsData?.location_info || '',
      [EQUIPMENT_DETAILS_FIELDS.LOCATION_ID]: locationId || '',
      [EQUIPMENT_DETAILS_FIELDS.WO_ASSET_ID]: detailsData?.wo_asset_id || '',
    },
  });

  const submit = handleSubmit((formData) => {
    if (!(equipmentType === 'repeater' || equipmentType === 'gateway' || equipmentType === 'node')) {
      delete formData.wo_asset_id;
    }

    locationChangeMutate(
      {
        equipmentType: equipmentType === 'repeater' ? 'router' : equipmentType,
        itemId,
        formData,
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('The updates have been saved.') });

          reset(formData);
        },
      },
    );
  });

  const handleOpenEdit = () => {
    setActiveModal({
      name: MODAL_NAMES.editPhotosModal,
      options: {
        photos: detailsData?.photos,
        equipmentType: equipmentType,
        equipmentId: detailsData?.equipment_id,
        itemId: itemId,
      },
    });
  };

  const copySerial = (serial: number) => {
    try {
      navigator.clipboard.writeText(`${serial}`).then(() => {
        showToast({ variant: 'success', message: t('Successfully copied to clipboard') });
      });
    } catch (e) {
      console.log(e);
    }
  };

  if (isEquipmentLoading) {
    return <EquipmentDetailsInformationSkeleton />;
  }

  return (
    <div>
      <div className={style.blockTitle}>
        {t('Technical information')}
        <ButtonCopyDetails copyDetailsData={copyDetailsData} tooltipInfo={t('Copy Technical information.')} />
      </div>
      <div className={style.blockContainer}>
        <div className={style.extraInfo}>
          <EquipmentDetailsType equipmentType={equipmentType} />
          <InfoField
            label={t('Serial Number')}
            value={detailsData?.serial || '-'}
            rightElement={
              detailsData?.serial ? (
                <CopyIcon className={style.copyIcon} onClick={() => copySerial(detailsData?.serial)} />
              ) : undefined
            }
          />
          {hasPermissions && equipmentType !== 'tachometer' ? (
            <div className={style.assetField}>
              <Input
                name={EQUIPMENT_DETAILS_FIELDS.WO_ASSET_ID}
                control={control}
                label={t('Asset code')}
                placeholder={t('Enter Asset Code')}
                type={'woHelperText'}
              />
            </div>
          ) : (
            <InfoField
              label={t('Asset code')}
              value={detailsData?.wo_asset_id || detailsData?.equipment?.asset_code || '-'}
            />
          )}
          {(equipmentType === 'repeater' || equipmentType === 'node') && (
            <InfoField label={t('Gateway serial number')} value={detailsData?.gateway?.serial || '-'} />
          )}
          {equipmentType === 'tachometer' && (
            <InfoField
              label={t('Node')}
              value={detailsData?.sensor?.node?.serial || '-'}
              rightElement={
                detailsData?.sensor?.node?.serial ? (
                  <CopyIcon
                    className={style.copyIcon}
                    onClick={() => copySerial(detailsData?.sensor?.node?.serial)}
                  />
                ) : undefined
              }
            />
          )}
        </div>
        <div className={style.mainInfo}>
          {equipmentType === 'gateway' && (
            <div className={style.twoFields}>
              <InfoField label={t('Name')} value={detailsData?.name || '-'} />
              <InfoField label={t('Id')} value={detailsData?.gateway_id || '-'} />
            </div>
          )}
          <div className={style.twoFields}>
            {equipmentType !== 'tachometer' && hasPermissions ? (
              <TreeSelect
                items={selectOptions}
                name={EQUIPMENT_DETAILS_FIELDS.LOCATION_ID}
                defaultSelectedName={locationName}
                searchable={true}
                label={t('Location')}
                control={control}
                fieldNames={{
                  children: 'children',
                  title: 'name',
                  key: 'id',
                }}
                placeholder={t('Start To Type...')}
                type={'woHelperText'}
              />
            ) : (
              <InfoField label={t('Location')} value={locationName || '-'} />
            )}
            <InfoField label={t('Id')} value={locationId || '-'} />
          </div>
          {equipmentType !== 'tachometer' && (
            <div className={cx(style.infoItem, { [style.infoItemField]: hasPermissions })}>
              {hasPermissions ? (
                <Input
                  name={EQUIPMENT_DETAILS_FIELDS.LOCATION_INFO}
                  control={control}
                  label={t('Location Info')}
                  placeholder={t('Enter location info')}
                  type={'woHelperText'}
                />
              ) : (
                <InfoField label={t('Location Info')} value={detailsData?.location_info || '-'} />
              )}
            </div>
          )}
          {(equipmentType === 'repeater' || equipmentType === 'node') && (
            <div className={style.twoFields}>
              <InfoField label={t('Gateway')} value={detailsData?.gateway?.name || '-'} />
              <InfoField label={t('Id')} value={detailsData?.gateway?.gateway_id || '-'} />
            </div>
          )}
          {equipmentType === 'tachometer' && (
            <div className={style.twoFields}>
              <InfoField
                label={t('Facility')}
                value={detailsData?.equipment?.locationTable?.facility?.name || '-'}
              />
              <InfoField
                label={t('Id')}
                value={detailsData?.equipment?.locationTable?.facility?.facility_id || '-'}
              />
            </div>
          )}
          {isDirty && (
            <div className={style.btnBlock}>
              <Button
                type="secondary"
                text={t('Cancel')}
                disabled={locationChangeIsLoading}
                onClick={() => reset()}
              />
              <Button
                type="primary"
                variant={'medium'}
                text={t('Save')}
                disabled={isSubmitting}
                onClick={submit}
              />
            </div>
          )}
        </div>
        <div className={style.galleryBlock}>
          {!isFetchingImages ? (
            <Gallery
              images={imagesList || []}
              variant={'equipment'}
              editEnabled={profileData?.userAccess?.manageEquipmentDeactivation}
              onEditPhotos={handleOpenEdit}
            />
          ) : (
            <SkeletonBlock className={style.skeletonGallery} />
          )}
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetailsInformation;
